import { BuddyMailAttachment } from '../entities/attachment.js';
import { map, of } from 'rxjs';
import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/bh-util/server/entity-reads.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';

export const getBuddyMailAttachmentsByIDs = registerQuery({
  operationName: 'getBuddyMailAttachmentsByIDs',
  type: () => [BuddyMailAttachment],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<BuddyMailAttachment>>(gqlFields: SelSet) =>
      readEntitiesByIDs(BuddyMailAttachment, ids, gqlFields),
});

export const subscribeBuddyMailAttachmentsByIDs = registerSubscription({
  operationName: 'subscribeBuddyMailAttachmentsByIDs',
  type: () => [BuddyMailAttachment],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<BuddyMailAttachment>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(BuddyMailAttachment, ids, gqlFields),
});

export const getAllBuddyMailAttachments = registerQuery({
  operationName: 'getAllBuddyMailAttachments',
  type: () => [BuddyMailAttachment],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<BuddyMailAttachment>>(gqlFields: SelSet) =>
      readAllEntities(BuddyMailAttachment, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllBuddyMailAttachments = registerSubscription({
  operationName: 'subscribeAllBuddyMailAttachments',
  type: () => [BuddyMailAttachment],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<BuddyMailAttachment>>(gqlFields: SelSet) =>
      watchAllEntities(BuddyMailAttachment, gqlFields, { skip, limit, excludeIDs }),
});

export const getBuddyMailAttachmentByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<BuddyMailAttachment>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getBuddyMailAttachmentsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeBuddyMailAttachmentByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<BuddyMailAttachment>>(gqlFields: SelSet) =>
    id ? subscribeBuddyMailAttachmentsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
